import {
  CalendarDays,
  Grid2x2,
  Lightbulb,
  Pencil,
  Plus,
  ShieldCheck,
  Swords,
  TriangleAlert,
  Zap,
  type LucideIcon,
} from "lucide-react";
import {
  useDelete,
  useGetList,
  useLocaleState,
  useNotify,
  useTranslate,
} from "ra-core";
import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";

import {
  EmptyState,
  ExternalAnchor,
  LoadingCards,
} from "@/components/market-intel/components";
import { formatShortDate } from "@/components/market-intel/dates";
import { SELECT_CLASS } from "@/components/market-intel/FormDialog";
import { MiniMarkdown } from "@/components/market-intel/MiniMarkdown";
import type { MarketCompetitor } from "@/components/market-intel/types";
import { riseStyle } from "@/components/market-intel/util";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

import { LineBadge } from "./CompetitorsView";
import { competitorPath } from "./routes";
import {
  itemsByQuadrant,
  selectAnalysis,
  sortAnalyses,
  SWOT_QUADRANTS,
  swotQuadrantLabelKey,
} from "./swot";
import { SwotAnalysisForm, SwotItemForm } from "./SwotForms";
import type { MarketSwotAnalysis, MarketSwotItem, SwotQuadrant } from "./types";
import { ConfirmDeleteButton, IconAction } from "./ui";

const QUADRANT_STYLE: Record<
  SwotQuadrant,
  { icon: LucideIcon; card: string; accent: string }
> = {
  fortaleza: {
    icon: ShieldCheck,
    card: "bg-emerald-500/8 ring-emerald-500/20 dark:bg-emerald-400/10 dark:ring-emerald-400/25",
    accent:
      "bg-emerald-600 text-white dark:bg-emerald-400 dark:text-emerald-950",
  },
  debilidad: {
    icon: TriangleAlert,
    card: "bg-amber-500/10 ring-amber-500/25 dark:bg-amber-400/10 dark:ring-amber-400/25",
    accent: "bg-amber-500 text-amber-950 dark:bg-amber-300 dark:text-amber-950",
  },
  oportunidad: {
    icon: Lightbulb,
    card: "bg-sky-500/8 ring-sky-500/20 dark:bg-sky-400/10 dark:ring-sky-400/25",
    accent: "bg-sky-600 text-white dark:bg-sky-300 dark:text-sky-950",
  },
  amenaza: {
    icon: Zap,
    card: "bg-red-500/8 ring-red-500/20 dark:bg-red-400/10 dark:ring-red-400/25",
    accent: "bg-red-600 text-white dark:bg-red-400 dark:text-red-950",
  },
};

type Editing =
  | { kind: "analysis"; analysis: MarketSwotAnalysis | null }
  | {
      kind: "item";
      item: MarketSwotItem | null;
      quadrant: SwotQuadrant;
    };

/** /competencia/foda: SWOT analyses (latest first) as a 2x2 grid. */
export const SwotView = () => {
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const [params, setParams] = useSearchParams();
  const [editing, setEditing] = useState<Editing | null>(null);
  const { data, isPending, error } = useGetList<MarketSwotAnalysis>(
    "market_swot_analyses",
    {
      pagination: { page: 1, perPage: 200 },
      sort: { field: "analysis_date", order: "DESC" },
    },
  );
  const analyses = useMemo(() => sortAnalyses(data ?? []), [data]);
  const selected = selectAnalysis(analyses, params.get("analisis"));
  const { data: items } = useGetList<MarketSwotItem>(
    "market_swot_items",
    {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "position", order: "ASC" },
      filter: { analysis_id: selected?.id },
    },
    { enabled: selected != null },
  );
  const { data: competitors } = useGetList<MarketCompetitor>(
    "market_competitors",
    {
      pagination: { page: 1, perPage: 500 },
      sort: { field: "name", order: "ASC" },
    },
  );
  const select = (id: string) => setParams({ analisis: id }, { replace: true });
  const addLabel = translate("competition.swot.add");
  const addButton = (
    <Button
      type="button"
      onClick={() => setEditing({ kind: "analysis", analysis: null })}
      aria-label={isMobile ? addLabel : undefined}
      title={isMobile ? addLabel : undefined}
      className={cn(
        "btn-save shrink-0 rounded-full",
        isMobile ? "size-9 p-0" : "px-4",
      )}
    >
      <Plus aria-hidden className={isMobile ? "size-5" : "size-4"} />
      {isMobile ? null : addLabel}
    </Button>
  );

  const forms = (
    <>
      <SwotAnalysisForm
        open={editing?.kind === "analysis"}
        onOpenChange={(open) => !open && setEditing(null)}
        analysis={editing?.kind === "analysis" ? editing.analysis : null}
        onCreated={(created) => select(String(created.id))}
      />
      {selected ? (
        <SwotItemForm
          open={editing?.kind === "item"}
          onOpenChange={(open) => !open && setEditing(null)}
          analysisId={selected.id}
          item={editing?.kind === "item" ? editing.item : null}
          quadrant={editing?.kind === "item" ? editing.quadrant : "fortaleza"}
          items={items ?? []}
          competitors={competitors ?? []}
        />
      ) : null}
    </>
  );

  if (isPending) return <LoadingCards count={2} />;
  if (error)
    return <EmptyState icon={Grid2x2} title={translate("market.error")} />;
  if (!selected)
    return (
      <>
        <EmptyState
          icon={Grid2x2}
          title={translate("competition.swot.empty_all")}
          hint={translate("competition.swot.empty_hint")}
          action={addButton}
        />
        {forms}
      </>
    );

  const grouped = itemsByQuadrant(items ?? []);
  return (
    <section className="space-y-3 md:space-y-4">
      <h2 className="sr-only">{translate("competition.swot.heading")}</h2>
      <div className="flex items-center gap-2">
        {analyses.length > 1 ? (
          <label className="min-w-0 flex-1 md:max-w-md">
            <span className="sr-only">
              {translate("competition.swot.pick")}
            </span>
            <select
              className={cn(
                SELECT_CLASS,
                "liquid-glass h-10 rounded-full border-0 px-4 md:h-9",
              )}
              value={String(selected.id)}
              onChange={(event) => select(event.target.value)}
            >
              {analyses.map((analysis) => (
                <option key={analysis.id} value={String(analysis.id)}>
                  {analysis.title}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <span className="flex-1" />
        )}
        {addButton}
      </div>
      <AnalysisHeader
        analysis={selected}
        count={items?.length ?? 0}
        onEdit={() => setEditing({ kind: "analysis", analysis: selected })}
        onDeleted={() => setParams({}, { replace: true })}
      />
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-4">
        {SWOT_QUADRANTS.map((quadrant, index) => (
          <Quadrant
            key={quadrant}
            quadrant={quadrant}
            items={grouped[quadrant]}
            competitors={competitors ?? []}
            index={index}
            onAdd={() => setEditing({ kind: "item", item: null, quadrant })}
            onEdit={(item) => setEditing({ kind: "item", item, quadrant })}
          />
        ))}
      </div>
      {forms}
    </section>
  );
};

const AnalysisHeader = ({
  analysis,
  count,
  onEdit,
  onDeleted,
}: {
  analysis: MarketSwotAnalysis;
  count: number;
  onEdit: () => void;
  onDeleted: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [locale = "es"] = useLocaleState();
  const [remove, { isPending }] = useDelete<MarketSwotAnalysis>();
  return (
    <article className="mi-card mi-rise min-w-0 space-y-2 p-4 md:p-5">
      <div className="flex min-w-0 items-start gap-2">
        <h3 className="min-w-0 flex-1 pt-1 text-lg leading-snug font-semibold tracking-tight wrap-anywhere md:text-xl">
          {analysis.title}
        </h3>
        <IconAction
          label={translate("competition.swot.edit")}
          icon={Pencil}
          onClick={onEdit}
        />
        <ConfirmDeleteButton
          label={translate("competition.swot.delete")}
          disabled={isPending}
          onConfirm={() =>
            remove(
              "market_swot_analyses",
              { id: analysis.id, previousData: analysis },
              {
                onSuccess: () => {
                  notify("competition.swot.deleted", { type: "info" });
                  onDeleted();
                },
                onError: () =>
                  notify("competition.form.error", { type: "error" }),
              },
            )
          }
        />
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm text-muted-foreground">
        {analysis.line ? <LineBadge line={analysis.line} /> : null}
        {analysis.segment ? (
          <span className="font-medium text-foreground">
            {analysis.segment}
          </span>
        ) : null}
        <span className="inline-flex items-center gap-1">
          <CalendarDays aria-hidden className="size-3.5" />
          {translate("competition.swot.dated", {
            date: formatShortDate(analysis.analysis_date, locale),
          })}
        </span>
        <span>
          {translate("competition.swot.items", { smart_count: count })}
        </span>
      </div>
      {analysis.source ? (
        <p className="text-sm">
          {analysis.source_url ? (
            <ExternalAnchor href={analysis.source_url}>
              {translate("competition.swot.source", {
                source: analysis.source,
              })}
            </ExternalAnchor>
          ) : (
            <span className="text-muted-foreground">
              {translate("competition.swot.source", {
                source: analysis.source,
              })}
            </span>
          )}
        </p>
      ) : null}
      {analysis.summary ? (
        <MiniMarkdown source={analysis.summary} className="text-[15px]" />
      ) : null}
    </article>
  );
};

const Quadrant = ({
  quadrant,
  items,
  competitors,
  index,
  onAdd,
  onEdit,
}: {
  quadrant: SwotQuadrant;
  items: MarketSwotItem[];
  competitors: readonly MarketCompetitor[];
  index: number;
  onAdd: () => void;
  onEdit: (item: MarketSwotItem) => void;
}) => {
  const translate = useTranslate();
  const { icon: Icon, card, accent } = QUADRANT_STYLE[quadrant];
  const title = translate(swotQuadrantLabelKey(quadrant));
  const titleId = `swot-${quadrant}`;
  return (
    <section
      aria-labelledby={titleId}
      data-quadrant={quadrant}
      className={cn(
        "mi-rise min-w-0 rounded-[1.375rem] p-4 ring-1 ring-inset md:p-5",
        card,
      )}
      style={riseStyle(index + 1)}
    >
      <header className="mb-2.5 flex items-center gap-2">
        <span
          aria-hidden
          className={cn(
            "flex size-8 shrink-0 items-center justify-center rounded-xl",
            accent,
          )}
        >
          <Icon className="size-4" />
        </span>
        <h3
          id={titleId}
          className="min-w-0 flex-1 truncate text-base font-semibold"
        >
          {title}
          <span className="ml-2 text-sm font-semibold text-muted-foreground tabular-nums">
            {items.length}
          </span>
        </h3>
        <IconAction
          label={`${translate("competition.swot.add_item")}: ${title}`}
          icon={Plus}
          onClick={onAdd}
          className="bg-background/60 text-foreground dark:bg-white/10"
        />
      </header>
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {translate("competition.swot.empty_quadrant")}
        </p>
      ) : (
        <ul className="space-y-1.5">
          {items.map((item) => (
            <SwotItemRow
              key={item.id}
              item={item}
              competitor={competitors.find(
                (competitor) =>
                  String(competitor.id) === String(item.competitor_id),
              )}
              onEdit={() => onEdit(item)}
            />
          ))}
        </ul>
      )}
    </section>
  );
};

const SwotItemRow = ({
  item,
  competitor,
  onEdit,
}: {
  item: MarketSwotItem;
  competitor?: MarketCompetitor;
  onEdit: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [remove, { isPending }] = useDelete<MarketSwotItem>();
  return (
    <li className="flex min-w-0 items-start gap-1 rounded-xl bg-background/70 py-1.5 pr-1 pl-3 dark:bg-white/6">
      <div className="min-w-0 flex-1 py-1">
        <p className="text-[15px] leading-snug wrap-anywhere">{item.content}</p>
        {competitor ? (
          <Link
            to={competitorPath(competitor.id)}
            className="mt-1 inline-flex max-w-full items-center gap-1 text-xs font-semibold text-primary hover:underline"
          >
            <Swords aria-hidden className="size-3 shrink-0" />
            <span className="truncate">{competitor.name}</span>
          </Link>
        ) : null}
      </div>
      <IconAction
        label={`${translate("competition.swot.edit_item")}: ${item.content}`}
        icon={Pencil}
        onClick={onEdit}
        className="size-8"
      />
      <ConfirmDeleteButton
        label={`${translate("competition.swot.delete_item")}: ${item.content}`}
        disabled={isPending}
        className="h-8 min-w-8 px-2"
        onConfirm={() =>
          remove(
            "market_swot_items",
            { id: item.id, previousData: item },
            {
              mutationMode: "optimistic",
              onSuccess: () =>
                notify("competition.form.deleted", { type: "info" }),
              onError: () =>
                notify("competition.form.error", { type: "error" }),
            },
          )
        }
      />
    </li>
  );
};
